import {
  BMC_CORE, BMC_LABEL, BMC_PREFIX, BMC_REST, bmcIndex, removeBmcItem, toggleBmcLink,
  type Bmc, type BmcBlock, type BmcItem, type BmcListBlock,
} from '@ads/shared';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as Ic from '../components/icons';

/* ─────────────────────────────────────────────────────────────────────
   Peças do Business Model Canvas: os blocos, os itens e as relações.

   Passar o mouse (ou o foco) num item acende, com a mesma varredura de
   luz dos blocos do Início, os itens de outros blocos ligados a ele — e
   esmaece o resto. O ícone de elo de cada item abre a lista de relações,
   para ligar e desligar.
   ───────────────────────────────────────────────────────────────────── */

export const BMC_ICON: Record<BmcBlock, (p: object) => ReactNode> = {
  segments: Ic.Target, relationship: Ic.Heart, channels: Ic.Link, revenue: Ic.Bars,
  resources: Ic.Cube, activities: Ic.Gear, partners: Ic.Branch, costs: Ic.Doc, valueProp: Ic.Star,
};
export const BMC_ASK: Record<BmcBlock, string> = {
  segments: 'Para quem criamos valor?', relationship: 'Como nos relacionamos com cada segmento?',
  channels: 'Por onde a proposta de valor chega aos clientes?', revenue: 'Como a organização gera receita?',
  resources: 'O que é indispensável para entregar?', activities: 'O que precisamos fazer muito bem?',
  partners: 'Quem nos ajuda a entregar?', costs: 'Quais são os principais custos da operação?',
  valueProp: 'Qual valor único oferecemos aos clientes?',
};
const PLACEHOLDER: Record<BmcListBlock, string> = {
  segments: 'Ex.: Famílias com filhos no ensino fundamental', relationship: 'Ex.: Atendimento consultivo',
  channels: 'Ex.: Site e redes sociais', revenue: 'Ex.: Mensalidades', resources: 'Ex.: Equipe especializada',
  activities: 'Ex.: Formação da equipe', partners: 'Ex.: Universidades', costs: 'Ex.: Pessoal e encargos',
};
const ORDER: BmcListBlock[] = [...BMC_CORE, ...BMC_REST];

/* ── o hover das relações ─────────────────────────────────────────── */

interface Rel { hover: string | null; set: (id: string | null) => void; related: Set<string> }
const RelCtx = createContext<Rel>({ hover: null, set: () => {}, related: new Set() });

export function RelationsProvider({ canvas, children }: { canvas: Bmc; children: ReactNode }) {
  const [hover, setHover] = useState<string | null>(null);
  const leave = useRef<ReturnType<typeof setTimeout>>(undefined);
  const idx = useMemo(() => bmcIndex(canvas), [canvas]);
  const related = useMemo(() => new Set(hover ? idx.get(hover)?.item.links ?? [] : []), [hover, idx]);
  /* ao sair, um instante de espera: atravessar o vão entre dois itens não pisca a tela */
  const set = (id: string | null) => {
    clearTimeout(leave.current);
    if (id) setHover(id);
    else leave.current = setTimeout(() => setHover(null), 140);
  };
  useEffect(() => () => clearTimeout(leave.current), []);
  return <RelCtx.Provider value={{ hover, set, related }}>{children}</RelCtx.Provider>;
}
export const useRelations = () => useContext(RelCtx);

function relClass(rel: Rel, id: string | undefined) {
  if (!rel.hover || !id) return '';
  if (rel.hover === id) return ' hot';
  return rel.related.has(id) ? ' rel' : ' dim';
}

/* ── um bloco com a sua lista ─────────────────────────────────────── */

export function BmcBlockCard({ block, title, canvas, onChange, action, busy, compact }: {
  block: BmcListBlock; title?: string; canvas: Bmc; onChange: (next: Bmc) => void;
  /** um botão no cabeçalho (ex.: "Sugerir") */
  action?: ReactNode; busy?: boolean; compact?: boolean;
}) {
  const rel = useRelations();
  const Icon = BMC_ICON[block];
  const items = canvas[block];
  const lit = !!rel.hover && items.some(i => i.id === rel.hover || rel.related.has(i.id!));
  const add = () => {
    const id = `${BMC_PREFIX[block]}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    onChange({ ...canvas, [block]: [...items, { id, text: '', links: [], ai: false }] });
    requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>(`[data-item="${id}"] textarea`)?.focus());
  };
  return (
    <section className={`card bmc-card b-${block}${lit ? ' lit' : ''}${compact ? ' compact' : ''}`}>
      <header className="bmc-card-h">
        <span className="bmc-ic"><Icon /></span>
        <div><h3>{title ?? BMC_LABEL[block]}</h3><p>{BMC_ASK[block]}</p></div>
        {action}
        <button type="button" className="bmc-add" onClick={add} aria-label={`Adicionar em ${BMC_LABEL[block]}`} disabled={items.length >= 16}><Ic.Plus /></button>
      </header>
      {busy && !items.length ? <div className="obj-skel bmc-skel"><i /><i /><i /></div> : (
        <ul className="bmc-items">
          {items.map(i => <ItemRow key={i.id} block={block} item={i} canvas={canvas} onChange={onChange} />)}
          {!items.length && <li className="bmc-empty"><button type="button" onClick={add}><Ic.Plus />{PLACEHOLDER[block]}</button></li>}
        </ul>
      )}
    </section>
  );
}

function ItemRow({ block, item, canvas, onChange }: { block: BmcListBlock; item: BmcItem; canvas: Bmc; onChange: (next: Bmc) => void }) {
  const rel = useRelations();
  const [open, setOpen] = useState(false);
  const id = item.id!;
  const patch = (text: string) => onChange({ ...canvas, [block]: canvas[block].map(i => (i.id === id ? { ...i, text, ai: false } : i)) });
  return (
    <li className={`bi${item.ai ? ' ai' : ''}${relClass(rel, id)}${open ? ' open' : ''}`} data-item={id}
      onMouseEnter={() => rel.set(id)} onMouseLeave={() => rel.set(null)}
      onFocus={() => rel.set(id)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) rel.set(null); }}>
      {item.ai && <span className="bi-ai" title="Sugerido pela IA — edite à vontade"><Ic.Sparkle /></span>}
      <textarea className="bi-in" rows={1} value={item.text} maxLength={200} placeholder={PLACEHOLDER[block]} aria-label={`${BMC_LABEL[block]}: item`}
        onChange={e => patch(e.target.value.replace(/\n/g, ' '))}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
      <button type="button" className={`bi-link${item.links.length ? ' on' : ''}`} onClick={() => setOpen(o => !o)}
        aria-label={`Relações de ${item.text || 'este item'}`} aria-expanded={open} title="Relações com outros blocos">
        <Ic.Link />{item.links.length > 0 && <b>{item.links.length}</b>}
      </button>
      <button type="button" className="bi-x" onClick={() => onChange(removeBmcItem(canvas, id))} aria-label={`Excluir ${item.text || 'item'}`}><Ic.Close /></button>
      {open && <LinkPicker block={block} item={item} canvas={canvas} onChange={onChange} onClose={() => setOpen(false)} />}
    </li>
  );
}

/** A lista de relações de um item: os itens dos outros blocos, para ligar e desligar. */
function LinkPicker({ block, item, canvas, onChange, onClose }: { block: BmcListBlock; item: BmcItem; canvas: Bmc; onChange: (next: Bmc) => void; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (box.current && !box.current.parentElement!.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    addEventListener('mousedown', down); addEventListener('keydown', key);
    return () => { removeEventListener('mousedown', down); removeEventListener('keydown', key); };
  }, [onClose]);
  const groups = ORDER.filter(b => b !== block).map(b => ({ b, items: canvas[b].filter(i => i.text.trim()) })).filter(g => g.items.length);
  return (
    <div className="bi-pick" ref={box} role="dialog" aria-label={`Relações de ${item.text}`}>
      <p className="bi-pick-h">Relaciona-se com…</p>
      {!groups.length && <p className="bi-pick-e">Preencha outros blocos para criar relações.</p>}
      {groups.map(g => (
        <div key={g.b} className="bi-pick-g">
          <em>{BMC_LABEL[g.b]}</em>
          {g.items.map(o => {
            const on = item.links.includes(o.id!);
            return (
              <label key={o.id} className={on ? 'on' : ''}>
                <input type="checkbox" checked={on} onChange={e => onChange(toggleBmcLink(canvas, item.id!, o.id!, e.target.checked))} />
                <span>{o.text}</span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ── só leitura: itens que acendem as relações ────────────────────── */

export function ItemChips({ items, variant = 'chip' }: { items: BmcItem[]; variant?: 'chip' | 'line' }) {
  const rel = useRelations();
  const shown = items.filter(i => i.text.trim());
  if (!shown.length) return <p className="bmc-none">a preencher</p>;
  return (
    <ul className={`bmc-${variant}s`}>
      {shown.map(i => (
        <li key={i.id} tabIndex={0} className={`bc${relClass(rel, i.id)}${i.ai ? ' ai' : ''}`}
          onMouseEnter={() => rel.set(i.id!)} onMouseLeave={() => rel.set(null)} onFocus={() => rel.set(i.id!)} onBlur={() => rel.set(null)}
          title={i.links.length ? `${i.links.length} ${i.links.length === 1 ? 'relação' : 'relações'}` : undefined}>
          {i.text}{i.links.length > 0 && <sup>{i.links.length}</sup>}
        </li>
      ))}
    </ul>
  );
}
